import { Injectable, inject } from '@angular/core';
import { Either, Left, Right } from 'purify-ts';
import { isOutOfWindowError } from '../../../domain/core/api-error';
import { ConversationsRepositoryFailure } from '../../../domain/conversations/failure';
import { IConversationsRepository } from '../../../domain/conversations/i-conversations-repository';
import {
  ContactName,
  Conversation,
  ListConversationsOptions,
  ListMessagesOptions,
  MarkConversationAsReadProps,
  MarkedAsRead,
  Message,
  Paginated,
  SendMessageProps,
  SentMessage,
  UpdateContactNameProps,
} from '../../../domain/conversations/types';
import { ApiResponse } from '../../../util/type';
import { IHttpClient } from '../../services/httpClient/interface';
import { withQuery } from '../util';

@Injectable({
  providedIn: 'root',
})
export class RealConversationsRepository implements IConversationsRepository {
  private httpClient = inject(IHttpClient);

  public async getConversations(
    options: ListConversationsOptions
  ): Promise<Either<ConversationsRepositoryFailure, Paginated<Conversation>>> {
    try {
      const url = withQuery('/whatsapp/conversations', {
        companyPhoneNumber: options.companyPhoneNumber,
        cursor: options.cursor,
        limit: options.limit,
      });

      const { body } = await this.httpClient.get<ApiResponse<Paginated<Conversation>>>(url);
      return Right(body.data);
    } catch (error) {
      return Left(
        ConversationsRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível carregar as conversas.',
        })
      );
    }
  }

  public async getMessages(
    options: ListMessagesOptions
  ): Promise<Either<ConversationsRepositoryFailure, Paginated<Message>>> {
    try {
      const url = withQuery(
        `/whatsapp/conversations/${encodeURIComponent(options.clientPhoneNumber)}/messages`,
        {
          companyPhoneNumber: options.companyPhoneNumber,
          cursor: options.cursor,
          limit: options.limit,
        }
      );

      const { body } = await this.httpClient.get<ApiResponse<Paginated<Message>>>(url);
      return Right(body.data);
    } catch (error) {
      return Left(
        ConversationsRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível carregar as mensagens desta conversa.',
        })
      );
    }
  }

  public async sendMessage(
    props: SendMessageProps
  ): Promise<Either<ConversationsRepositoryFailure, SentMessage>> {
    try {
      const { body } = await this.httpClient.post<ApiResponse<SentMessage>>(
        '/whatsapp/messages',
        props
      );
      return Right(body.data);
    } catch (error) {
      if (isOutOfWindowError(error)) {
        return Left(ConversationsRepositoryFailure.outOfConversationWindow());
      }

      return Left(
        ConversationsRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível enviar a mensagem.',
        })
      );
    }
  }

  public async markConversationAsRead(
    props: MarkConversationAsReadProps
  ): Promise<Either<ConversationsRepositoryFailure, MarkedAsRead>> {
    try {
      const { body } = await this.httpClient.post<ApiResponse<MarkedAsRead>>(
        `/whatsapp/conversations/${encodeURIComponent(props.clientPhoneNumber)}/read`,
        { companyPhoneNumber: props.companyPhoneNumber }
      );
      return Right(body.data);
    } catch (error) {
      return Left(
        ConversationsRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível marcar a conversa como lida.',
        })
      );
    }
  }

  public async updateContactName(
    props: UpdateContactNameProps
  ): Promise<Either<ConversationsRepositoryFailure, ContactName>> {
    try {
      const { body } = await this.httpClient.patch<ApiResponse<ContactName>>(
        `/whatsapp/contacts/${encodeURIComponent(props.clientPhoneNumber)}`,
        { chosenName: props.chosenName }
      );
      return Right(body.data);
    } catch (error) {
      return Left(
        ConversationsRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível salvar o nome do contato.',
        })
      );
    }
  }
}
